import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from './api';

function ContattiPagamenti({ onSelectionChange = () => {} }) {
  // Stati per i dati
  const [contatti, setContatti] = useState([]);
  const [filteredContatti, setFilteredContatti] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // 🔧 NUOVI STATI PER DATE (sostituiscono selectedMese)
  const [dataInizio, setDataInizio] = useState('');
  const [dataFine, setDataFine] = useState('');
  
  // Stati per i filtri
  const [searchEnte, setSearchEnte] = useState('');
  const [searchProvincia, setSearchProvincia] = useState('');
  const [searchTipoEnte, setSearchTipoEnte] = useState('');
  const [searchPagamento, setSearchPagamento] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  
  // Stati per la selezione
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // 🔧 NUOVI STATI PER ORDINAMENTO
  const [sortField, setSortField] = useState('data'); // Default: Data ASC
  const [sortDirection, setSortDirection] = useState('asc');

  // 🔧 INIZIALIZZAZIONE DATE DEFAULT (inizio/fine mese corrente)
  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    // Formato YYYY-MM-DD per input date
    const startString = firstDay.toISOString().split('T')[0];
    const endString = lastDay.toISOString().split('T')[0];
    
    setDataInizio(startString);
    setDataFine(endString);
    
    console.log('📅 Date inizializzate (mese corrente):', { dataInizio: startString, dataFine: endString });
  }, []);

  // Placeholders MEMOIZZATI - AGGIUNTO {QUOTA}
  const placeholders = useMemo(() => [
    '{TIPOENTE}',
    '{ENTE}', 
    '{PROVINCIA}',
    '{PAGAMENTO}',
    '{QUOTA}', // 🔧 NUOVO PLACEHOLDER
    '{DATA}',
    '{EMAIL}',
    '{FILEURL}',
    '{DATA_ODIERNA}',
    '{IMMAGINE_INTESTAZIONE}',
    '{IMMAGINE_FONDO}',
    '{FIRMA_ANUTEL}'
  ], []);

  // 🔧 FUNZIONE PER ESTRARRE QUOTA DALL'CAMPO PAGAMENTO
  const extractQuota = useCallback((pagamentoText) => {
    if (!pagamentoText) return 'QUOTA ANNO IN CORSO';
    
    // Cerca pattern "QUOTA XXXX" (QUOTA + spazio + 4 cifre)
    const quotaMatch = pagamentoText.match(/QUOTA\s+(\d{4})/i);
    
    if (quotaMatch) {
      return `QUOTA ${quotaMatch[1]}`; // Es: "QUOTA 2025"
    }
    
    // Fallback se non trova il pattern
    return 'QUOTA ANNO IN CORSO';
  }, []);

  // 🔧 CARICAMENTO DATI - MODIFICATO PER DATE E QUERYSTRING
  const loadContatti = useCallback(async (startDate, endDate) => {
    if (!startDate || !endDate) {
      console.warn('⚠️ Date non valide per il caricamento');
      return;
    }

    setLoading(true);
    setError('');
    setContatti([]);
    setFilteredContatti([]);
    setSelectedRows([]);
    
    // 🔧 RESET ORDINAMENTO E FILTRI QUANDO CAMBIA PERIODO
    setSortField('data');
    setSortDirection('asc');
    setSearchEnte('');
    setSearchProvincia('');
    setSearchTipoEnte('');
    setSearchPagamento('');
    setSearchEmail('');
    
    try {
      console.log(`🔄 Caricamento contatti pagamenti per periodo: ${startDate} - ${endDate}`);
      
      // 🔧 COSTRUISCE URL CON QUERYSTRING inizio e fine
      const url = `/api/Contact/pagamenti?inizio=${startDate}&fine=${endDate}`;
      console.log('📤 URL richiesta:', url);
      
      const response = await apiFetch(url);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📊 Dati ricevuti:', data);
        
        // Aggiungi ID univoci e trasforma in formato compatibile
        const contattiConId = (data || []).map((item, index) => {
          const quotaValue = extractQuota(item.pagamento); // 🔧 ESTRAI QUOTA
          
          return {
            id: `pag_${startDate}_${endDate}_${index}_${Date.now()}`, // ID univoco con periodo
            key: `pag_${startDate}_${endDate}_${index}_${Date.now()}`,
            tipoEnte: item.tipoEnte || '',
            ente: item.ente || '',
            provincia: item.provincia || '',
            pagamento: item.pagamento || '',
            quota: quotaValue, // 🔧 NUOVO CAMPO QUOTA
            data: item.data || '',
            emailOrd: [item.email].filter(Boolean),
            emailPec: [],
            email: item.email || '',
            fileUrl: item.fileUrl || '',
            // Flag per allegato tessera
            hasTessera: item.fileUrl && item.fileUrl.startsWith('tessera://')
          };
        });
        
        setContatti(contattiConId);
        setFilteredContatti(contattiConId);
        
        console.log(`✅ Caricati ${contattiConId.length} contatti pagamenti per periodo ${startDate} - ${endDate}`);
      } else {
        const errorText = await response.text();
        throw new Error(`Errore ${response.status}: ${errorText}`);
      }
    } catch (err) {
      console.error('❌ Errore caricamento contatti pagamenti:', err);
      setError(`Errore nel caricamento: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [extractQuota]);

  // 🔧 CARICAMENTO AUTOMATICO QUANDO LE DATE SONO DISPONIBILI
  useEffect(() => {
    if (dataInizio && dataFine) {
      loadContatti(dataInizio, dataFine);
    }
  }, []); // Solo al mount, dopo userà il button

  // 🔧 FUNZIONE ORDINAMENTO
  const handleSort = useCallback((field) => {
    setSortField(prevField => {
      setSortDirection(prevDirection => {
        // Se stessa colonna, cambia direzione; se colonna diversa, inizia con ASC
        if (prevField === field) {
          return prevDirection === 'asc' ? 'desc' : 'asc';
        } else {
          return 'asc';
        }
      });
      return field;
    });
  }, []);

  // 🔧 APPLICAZIONE ORDINAMENTO E FILTRI
  const applyFiltersAndSort = useCallback(() => {
    let filtered = [...contatti];
    
    // PRIMA: Applica filtri
    if (searchEnte.trim()) {
      filtered = filtered.filter(c => 
        c.ente && c.ente.toLowerCase().includes(searchEnte.toLowerCase())
      );
    }
    
    if (searchProvincia.trim()) {
      filtered = filtered.filter(c => 
        c.provincia && c.provincia.toLowerCase().includes(searchProvincia.toLowerCase())
      );
    }
    
    if (searchTipoEnte.trim()) {
      filtered = filtered.filter(c => 
        c.tipoEnte && c.tipoEnte.toLowerCase().includes(searchTipoEnte.toLowerCase())
      );
    }
    
    if (searchPagamento.trim()) {
      filtered = filtered.filter(c => 
        c.pagamento && c.pagamento.toLowerCase().includes(searchPagamento.toLowerCase())
      );
    }
    
    if (searchEmail.trim()) {
      filtered = filtered.filter(c => 
        c.email && c.email.toLowerCase().includes(searchEmail.toLowerCase())
      );
    }
    
    // SECONDA: Applica ordinamento
    if (sortField) {
      filtered.sort((a, b) => {
        let valueA = a[sortField];
        let valueB = b[sortField];
        
        // Gestione valori nulli/undefined
        if (valueA == null) valueA = '';
        if (valueB == null) valueB = '';
        
        // Conversione per ordinamento
        if (sortField === 'data') {
          // Ordinamento cronologico
          valueA = new Date(valueA).getTime() || 0;
          valueB = new Date(valueB).getTime() || 0;
        } else {
          // Ordinamento alfabetico (case insensitive)
          valueA = String(valueA).toLowerCase();
          valueB = String(valueB).toLowerCase();
        }
        
        // Confronto
        if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
        if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    setFilteredContatti(filtered);
    
    // Reset selezioni se i dati filtrati/ordinati cambiano
    setSelectedRows([]);
    setSelectAll(false);
  }, [contatti, searchEnte, searchProvincia, searchTipoEnte, searchPagamento, searchEmail, sortField, sortDirection]);

  // Applica filtri e ordinamento quando cambiano
  useEffect(() => {
    applyFiltersAndSort();
  }, [applyFiltersAndSort]);

  // Gestione selezione - CALLBACK MEMOIZZATI
  const handleRowSelection = useCallback((contatto, isSelected) => {
    setSelectedRows(prev => {
      let newSelected;
      if (isSelected) {
        newSelected = [...prev, contatto];
      } else {
        newSelected = prev.filter(r => r.id !== contatto.id);
      }
      return newSelected;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectAll(prev => {
      const newSelectAll = !prev;
      if (newSelectAll) {
        setSelectedRows([...filteredContatti]);
      } else {
        setSelectedRows([]);
      }
      return newSelectAll;
    });
  }, [filteredContatti]);

  // Aggiorna selectAll quando cambiano le selezioni
  useEffect(() => {
    setSelectAll(selectedRows.length === filteredContatti.length && filteredContatti.length > 0);
  }, [selectedRows.length, filteredContatti.length]);

  // COMUNICAZIONE CON PARENT - MEMOIZZATO CON DIPENDENZE STABILI
  useEffect(() => {
    const emailCount = selectedRows.reduce((total, row) => {
      return total + (row.emailOrd?.length || 0) + (row.emailPec?.length || 0);
    }, 0);

    const selectionData = {
      selectedRows: selectedRows,
      destinatariCount: selectedRows.length,
      emailCount,
      placeholders: placeholders
    };

    console.log('📬 ContattiPagamenti - Comunicazione con parent:', selectionData);
    onSelectionChange(selectionData);

  }, [selectedRows, placeholders, onSelectionChange]);

  // 🔧 VALIDAZIONE DATE
  const validateDates = () => {
    if (!dataInizio || !dataFine) {
      alert('Seleziona entrambe le date');
      return false;
    }
    
    if (dataInizio > dataFine) {
      alert('La data di inizio deve essere precedente alla data di fine');
      return false;
    }
    
    return true;
  };

  // 🔧 HANDLER AGGIORNAMENTO DATI
  const handleRefreshData = () => {
    if (validateDates()) {
      loadContatti(dataInizio, dataFine);
    }
  };

  // 🔧 FUNZIONE PER ICONE ORDINAMENTO
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <i className="fas fa-sort text-muted ms-1"></i>;
    }
    return sortDirection === 'asc' ? 
      <i className="fas fa-sort-up text-primary ms-1"></i> : 
      <i className="fas fa-sort-down text-primary ms-1"></i>;
  };

  return (
    <div className="card border-primary">
      <div className="card-header bg-light">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0 text-primary">
            <i className="fas fa-coins"></i> Contatti Pagamenti
          </h5>
          <div className="d-flex align-items-center">
            {selectedRows.length > 0 && (
              <span className="badge bg-success me-2">
                <i className="fas fa-check me-1"></i>
                {selectedRows.length} selezionati
              </span>
            )}
            <span className="badge bg-info">
              <i className="fas fa-list me-1"></i>
              {filteredContatti.length} elementi
            </span>
          </div>
        </div>
      </div>
      
      <div className="card-body">
        {/* 🔧 SELEZIONE PERIODO CON DATE - COME POLIZZE */}
        <div className="row mb-3">
          <div className="col-md-3">
            <label className="form-label fw-bold text-start w-100">
              <i className="fas fa-calendar me-1 text-muted"></i> Data Inizio:
            </label>
            <input
              type="date"
              className="form-control"
              value={dataInizio}
              onChange={(e) => setDataInizio(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label fw-bold text-start w-100">
              <i className="fas fa-calendar me-1 text-muted"></i> Data Fine:
            </label>
            <input
              type="date"
              className="form-control"
              value={dataFine}
              onChange={(e) => setDataFine(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="col-md-6 d-flex align-items-end">
            <button 
              className="btn btn-primary"
              onClick={handleRefreshData}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Caricamento...
                </>
              ) : (
                <>
                  <i className="fas fa-sync me-1"></i> Aggiorna Dati
                </>
              )}
            </button>
          </div>
        </div>

        {/* Filtri di ricerca */}
        <div className="row mb-3">
          <div className="col-md-2">
            <label className="form-label fw-bold text-start w-100">
              <i className="fas fa-building me-1 text-muted"></i> Tipo Ente:
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Filtra per tipo..."
              value={searchTipoEnte}
              onChange={(e) => setSearchTipoEnte(e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label fw-bold text-start w-100">
              <i className="fas fa-university me-1 text-muted"></i> Ente:
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Filtra per ente..."
              value={searchEnte}
              onChange={(e) => setSearchEnte(e.target.value)}
            />
          </div>
          <div className="col-md-1">
            <label className="form-label fw-bold text-start w-100">
              <i className="fas fa-map me-1 text-muted"></i> Pr:
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="PR"
              value={searchProvincia}
              onChange={(e) => setSearchProvincia(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label fw-bold text-start w-100">
              <i className="fas fa-coins me-1 text-muted"></i> Pagamento:
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Filtra per pagamento..."
              value={searchPagamento}
              onChange={(e) => setSearchPagamento(e.target.value)}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label fw-bold text-start w-100">
              <i className="fas fa-envelope me-1 text-muted"></i> Email:
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Filtra per email..."
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
            />
          </div>
        </div>

        {/* Stato caricamento/errori */}
        {loading && (
          <div className="text-center py-4">
            <div className="spinner-border text-primary me-2" role="status"></div>
            <span>Caricamento contatti pagamenti...</span>
          </div>
        )}

        {error && (
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {/* 🔧 TABELLA DATI - CON ORDINAMENTO CLICCABILE */}
        {!loading && !error && (
          <div className="table-responsive position-relative" style={{ maxHeight: 500, overflowY: 'auto' }}>
            <table className="table table-striped table-hover mb-0">
              <thead className="table-dark sticky-top">
                <tr>
                  <th style={{width: '50px'}}>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        disabled={filteredContatti.length === 0}
                      />
                    </div>
                  </th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('tipoEnte')}>
                    Tipo {getSortIcon('tipoEnte')}
                  </th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('ente')}>
                    Ente {getSortIcon('ente')}
                  </th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('provincia')}>
                    PR {getSortIcon('provincia')}
                  </th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('pagamento')}>
                    Pagamento {getSortIcon('pagamento')}
                  </th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('data')}>
                    Data {getSortIcon('data')}
                  </th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('email')}>
                    Email {getSortIcon('email')}
                  </th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('hasTessera')}>
                    Tessera {getSortIcon('hasTessera')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredContatti.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-muted">
                      <i className="fas fa-inbox me-2"></i>
                      Nessun pagamento trovato per il periodo selezionato
                    </td>
                  </tr>
                ) : (
                  filteredContatti.map((contatto) => (
                    <tr key={contatto.id}>
                      <td>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={selectedRows.some(r => r.id === contatto.id)}
                            onChange={(e) => handleRowSelection(contatto, e.target.checked)}
                          />
                        </div>
                      </td>
                      <td>
                        <span className="badge bg-secondary">{contatto.tipoEnte}</span>
                      </td>
                      <td className="fw-bold">{contatto.ente}</td>
                      <td>
                        <span className="badge bg-info">{contatto.provincia}</span>
                      </td>
                      <td>
                        <small className="text-success">{contatto.pagamento}</small>
                      </td>
                      <td>
                        <small className="text-muted">{contatto.data}</small>
                      </td>
                      <td>
                        <small>
                          <i className="fas fa-envelope text-primary me-1"></i>
                          {contatto.email}
                        </small>
                      </td>
                      <td>
                        {contatto.hasTessera ? (
                          <span className="badge bg-warning text-dark">
                            <i className="fas fa-file-pdf me-1"></i>
                            Tessera
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Info selezione */}
        {filteredContatti.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <small className="text-muted">
              <i className="fas fa-info-circle me-1"></i>
              Visualizzati {filteredContatti.length} di {contatti.length} elementi - Periodo: {dataInizio} / {dataFine}
            </small>
            {selectedRows.length > 0 && (
              <small className="text-success">
                <i className="fas fa-check-circle me-1"></i>
                {selectedRows.length} elementi selezionati - {
                  selectedRows.reduce((total, row) => total + (row.emailOrd?.length || 0), 0)
                } email da inviare
              </small>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ContattiPagamenti;